/**
 * LoanAmountSliderInput
 *
 * Generic slider component for loan amount selection.
 * Sends numeric values to orchestrator for data saving.
 *
 * Features:
 * - Slider from ₹25,000 to max_loan_amount (from backend)
 * - Step: ₹10,000
 * - Indian currency formatting
 * - Text input for manual entry
 * - Mobile-responsive
 * - Orchestrator-compatible
 * - Generates dynamic BRE loan offers based on slider amount
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { IndianRupee, FileText, Check, Loader2 } from 'lucide-react';
import KFSModal from '@/components/LOS/Caprion/KFSModal';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

interface LoanAmountSliderInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { loan_amount?: number | null; max_loan_amount?: number | null };
  onChange?: (val: { loan_amount: number }) => void;
  disabled?: boolean;
  // Legacy pattern
  initialData?: { loan_amount?: number; max_loan_amount?: number };
  onDataChange?: (data: { loan_amount: number }) => void;
}

const LoanAmountSliderInput: React.FC<LoanAmountSliderInputProps> = ({
  value,
  onChange,
  disabled = false,
  initialData,
  onDataChange
}) => {
  // Slider configuration
  const MIN_AMOUNT = 25000;      // ₹25,000
  const STEP = 10000;            // ₹10,000
  const DEFAULT_AMOUNT = 100000; // ₹1,00,000
  const DEFAULT_MAX = 500000;    // ₹5,00,000 (default if backend doesn't provide)

  // State management - max_loan_amount from backend (set once, doesn't change)
  const [maxAmount, setMaxAmount] = useState<number>(DEFAULT_MAX);
  const [currentAmount, setCurrentAmount] = useState<number>(DEFAULT_AMOUNT);
  const [inputValue, setInputValue] = useState<string>(''); // Separate state for input field
  const [isKFSModalOpen, setIsKFSModalOpen] = useState(false);
  const [initialOfferGenerated, setInitialOfferGenerated] = useState(false);
  const [isInputValid, setIsInputValid] = useState<boolean>(true);
  const [hasUserModified, setHasUserModified] = useState<boolean>(false); // Track if user is typing
  // Shown while an offer is being regenerated, so the user waits instead of
  // dragging again and stacking up more requests.
  const [isGeneratingOffer, setIsGeneratingOffer] = useState<boolean>(false);

  // Debounce timer MUST be a ref, not state. It used to be state, and because
  // setState is async every handleSliderChange saw a stale timer id — the
  // clearTimeout cleared nothing and one drag fired ~6 generate_offer_for_amount
  // calls. A ref is written synchronously, so the previous timer is always
  // cancelled and exactly one request goes out per drag.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against overlapping requests (e.g. debounce fires while the tick
  // button's request is still in flight) and lets a late response be ignored.
  const requestIdRef = useRef(0);

  // Initialize max amount ONCE from backend data
  useEffect(() => {
    if (value?.max_loan_amount !== undefined && value.max_loan_amount !== null) {
      setMaxAmount(value.max_loan_amount);
    } else if (initialData?.max_loan_amount) {
      setMaxAmount(initialData.max_loan_amount);
    }
  }, [value?.max_loan_amount, initialData?.max_loan_amount]);

  // Update current amount when value prop changes (but NOT max amount)
  // Only sync inputValue if it's from slider change, not from user typing
  useEffect(() => {
    if (value?.loan_amount !== undefined && value.loan_amount !== null) {
      setCurrentAmount(value.loan_amount);
      // Only update inputValue if it's different from what user typed (slider change)
      // Don't override user's typing
      if (inputValue === '' || parseInt(inputValue, 10) !== value.loan_amount) {
        setInputValue(value.loan_amount.toString());
        // Re-validate when syncing from parent
        const isValid = value.loan_amount >= MIN_AMOUNT && value.loan_amount <= maxAmount;
        setIsInputValid(isValid);
      }
    } else if (initialData?.loan_amount) {
      setCurrentAmount(initialData.loan_amount);
      if (inputValue === '' || parseInt(inputValue, 10) !== initialData.loan_amount) {
        setInputValue(initialData.loan_amount.toString());
        const isValid = initialData.loan_amount >= MIN_AMOUNT && initialData.loan_amount <= maxAmount;
        setIsInputValid(isValid);
      }
    }
  }, [value?.loan_amount, initialData?.loan_amount, maxAmount]);

  // Generate initial offer when component mounts — exactly once.
  // The ref (not the state flag) is the guard: state updates are async, so under
  // React StrictMode's double-mount both passes saw initialOfferGenerated=false
  // and fired generate_initial_offer twice.
  const initialOfferRequestedRef = useRef(false);
  useEffect(() => {
    if (initialOfferRequestedRef.current) return;
    initialOfferRequestedRef.current = true;
    generateInitialOffer();
  }, []);

  // Generate initial offer on page load
  const generateInitialOffer = async () => {
    try {
      console.log('[LoanAmountSlider] 🔄 Generating initial offer');
      const response = await axios.post(
        `${API_BASE_URL}/api/caprion/generate_initial_offer/`,
        {},
        { withCredentials: true }
      );
      console.log('[LoanAmountSlider] ✅ Initial offer generated:', response.data);
      setInitialOfferGenerated(true);
    } catch (error: any) {
      console.error('[LoanAmountSlider] ❌ Failed to generate initial offer:', error);
      console.error('Error response:', error.response?.data);
    }
  };
 
  // Cleanup debounce timer on unmount
  useEffect(() => {
  	return () => {
  		if (debounceTimerRef.current) {
  			clearTimeout(debounceTimerRef.current);
  		}
  	};
  }, []);

  // Single place that talks to the offer API, shared by the slider and the tick
  // button so they can never both fire for the same interaction.
  const generateOfferForAmount = useCallback(async (amount: number) => {
  	const requestId = ++requestIdRef.current;
  	setIsGeneratingOffer(true);
  	console.log('[LoanAmountSlider] 🔄 Generating offer for amount:', amount);
  	try {
  		const response = await axios.post(
  			`${API_BASE_URL}/api/caprion/generate_offer_for_amount/`,
  			{ loan_amount: amount },
  			{ withCredentials: true }
  		);
  		console.log('[LoanAmountSlider] ✅ Offer generated:', response.data);
  	} catch (error: any) {
  		console.error('[LoanAmountSlider] ❌ Failed to generate offer:', error);
  		console.error('Error response:', error.response?.data);
  	} finally {
  		// Only the newest request clears the loader — a slower earlier response
  		// must not unblock the UI while a fresher one is still running.
  		if (requestId === requestIdRef.current) {
  			setIsGeneratingOffer(false);
  		}
  	}
  }, []);
 
  // Handle slider change
  const handleSliderChange = useCallback((values: number[]) => {
  	const newAmount = values[0];
  	setCurrentAmount(newAmount);
  	
  	// Call both callbacks for compatibility
  	onChange?.({ loan_amount: newAmount });
  	onDataChange?.({ loan_amount: newAmount });
 
  	// Debounce: one API call once the user stops dragging. 550ms is short enough
  	// to feel responsive on release, yet still long enough to swallow the burst of
  	// onValueChange events fired while a finger is actually moving.
  	if (debounceTimerRef.current) {
  		clearTimeout(debounceTimerRef.current);
  	}
  	debounceTimerRef.current = setTimeout(() => {
  		debounceTimerRef.current = null;
  		generateOfferForAmount(newAmount);
  	}, 550);
  }, [onChange, onDataChange, generateOfferForAmount]);

  // Handle text input change - allow free entry, validate on tick button click
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
    setInputValue(rawValue);
    setHasUserModified(true); // Show button when user types
    
    // Validate if value is within range
    if (rawValue === '') {
      setIsInputValid(false);
      return;
    }
    
    const numValue = parseInt(rawValue, 10);
    const isValid = numValue >= MIN_AMOUNT && numValue <= maxAmount;
    console.log('[LoanAmountSlider] Validating:', { rawValue, numValue, MIN_AMOUNT, maxAmount, isValid });
    setIsInputValid(isValid);
  };

  // Handle blur - restore slider value if input is empty
  const handleInputBlur = () => {
    if (inputValue === '' || !isInputValid) {
      // Restore current slider value
      setInputValue(currentAmount.toString());
      setIsInputValid(true);
      setHasUserModified(false);
    }
  };

  // Handle tick button click - apply the input value
  const handleApplyAmount = useCallback(async () => {
    if (!isInputValid || inputValue === '') {
      return;
    }
    
    const newAmount = parseInt(inputValue, 10);
    
    // Update state
    setCurrentAmount(newAmount);
    onChange?.({ loan_amount: newAmount });
    onDataChange?.({ loan_amount: newAmount });
    setHasUserModified(false); // Hide button after applying

    // Cancel any pending slider-triggered call so the two paths don't both hit
    // the API for what the user experiences as a single change.
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    await generateOfferForAmount(newAmount);
  }, [inputValue, isInputValid, onChange, onDataChange, generateOfferForAmount]);

  // Format number in Indian style (without currency symbol)
  const formatNumber = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="space-y-2">
      {/* Full-page blocking overlay while the offer is regenerated. Covers the
          whole viewport (not just this component) because the tenure, EDI and
          KFS figures elsewhere on the page are also being recalculated — letting
          the user interact with stale numbers would be worse than making them
          wait. It also stops repeat dragging, which used to queue extra calls. */}
      {isGeneratingOffer && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-sm">
          <Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
          <p className="text-sm font-medium text-gray-700">Updating your offer...</p>
          <p className="text-xs text-gray-500">Please wait a moment</p>
        </div>
      )}

      <Label className="text-base font-medium text-gray-700">
        Loan Amount
      </Label>
      
      {/* Text input with rupee symbol and tick button */}
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
            <IndianRupee className="w-5 h-5 text-intellend-green" />
          </div>
          <Input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled}
            placeholder="Enter amount"
            className={`pl-10 pr-4 text-xl font-semibold text-center ${
              isInputValid
                ? 'text-intellend-green bg-intellend-green/5 border-intellend-green/20'
                : 'text-red-600 bg-red-50 border-red-300'
            }`}
          />
        </div>
        {/* Show button only when user has modified the input */}
        {hasUserModified && (
          <Button
            type="button"
            onClick={handleApplyAmount}
            disabled={disabled || !isInputValid || inputValue === ''}
            className="h-10 w-10 p-0 bg-intellend-green hover:bg-intellend-green/90 disabled:opacity-50 disabled:bg-gray-400 text-white"
          >
            <Check className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      {/* Validation message */}
      {!isInputValid && inputValue !== '' && (
        <p className="text-xs text-red-600 px-2">
          Amount must be between ₹{formatNumber(MIN_AMOUNT)} and ₹{formatNumber(maxAmount)}
        </p>
      )}

      {/* Slider */}
      <div className="px-2 py-2">
        <Slider
          value={[currentAmount]}
          onValueChange={handleSliderChange}
          min={MIN_AMOUNT}
          max={maxAmount}
          step={STEP}
          disabled={disabled || isGeneratingOffer}
          className="w-full"
        />
      </div>

      {/* Min/Max labels with centered helper text */}
      <div className="flex justify-between items-start text-sm text-gray-500 px-2 -mt-2">
        <span>₹{formatNumber(MIN_AMOUNT)}</span>
        
        {/* Helper text - Compact 2-line format centered between numbers */}
        <div className="text-xs text-gray-400 text-center leading-tight">
          <p>Drag the slider to select</p>
          <p>your desired loan amount</p>
        </div>
        
        <span>₹{formatNumber(maxAmount)}</span>
      </div>
    </div>
  );
};

export default LoanAmountSliderInput;
